/**
 * /dashboard/accounting/journals/$id — Journal entry detail (read surface).
 *
 * The header (entry #, date, narration, source, posted_at) + a balanced
 * Dr/Cr lines table (Account / Description / Debit / Credit, money
 * right-aligned) with a totals footer. Journals are IMMUTABLE (Companies
 * Act §128(1)) so this is strictly read-only — there is no edit / delete.
 *
 * When the entry was produced by the engine (source_type != "manual" and
 * source_id is set) a "View source document" link deep-links to the
 * originating invoice / bill / receipt / payment detail page so the
 * operator can trace the posting back to its source.
 *
 * Role-gate (accountingRole) + feature-gate (RequireFeature("accounting"))
 * are applied by the AccountingModuleShell.
 *
 * "Tally voucher" downloads this one entry as a TallyPrime-importable
 * voucher (GET /journal-entries/{id}/tally.xml). The same voucher is also
 * in the date-range export and (for salary journals) the payroll run's
 * Tally JV — the hint warns against importing it twice.
 */

import { journalSourceRoute } from "@/lib/accounting-gst";
import { useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { accountingApi, toApiError } from "@/lib/api";
import { formatPaise, fmtDate, fmtDateTime } from "@/lib/expense-format";
import { useJournalEntry } from "@/hooks/use-accounting";
import { useSmPlus } from "@/lib/use-sm-plus";
import type { JournalEntryDetail } from "@/types/accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/journals/$id",
)({
  component: JournalDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function sourceLabel(source: string): string {
  switch (source) {
    case "manual":
      return "Manual";
    case "invoice":
      return "Invoice";
    case "bill":
      return "Bill";
    case "receipt":
      return "Receipt";
    case "payment":
      return "Payment";
    default:
      return source
        .split("_")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(" ");
  }
}

function SourceBadge({ source }: { source: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {sourceLabel(source)}
    </span>
  );
}

/**
 * A small key/value pair for the header grid. Renders an em-dash for an
 * empty value so the layout never collapses.
 */
function HeaderField({ label, value }: { label: string; value?: string }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
    </div>
  );
}

/**
 * Deep-link the engine source document to its detail route. Returns null
 * for manual entries (no source) or any source_type we don't have a
 * detail route for — the caller hides the link in that case.
 */
function SourceDocumentLink({
  entry,
  tenantSlug,
}: {
  entry: JournalEntryDetail;
  tenantSlug: string;
}) {
  const linkClass =
    "inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline";
  // The server resolves the source document (with its number) — incl. a
  // payroll run behind a salary journal, which opens the read-only
  // payroll register.
  const resolved = journalSourceRoute(entry.source);
  if (entry.source && resolved) {
    return (
      <Link to={resolved.to} params={{ tenantSlug, id: resolved.id }} className={linkClass}>
        <ExternalLink aria-hidden className="h-4 w-4" />
        View {entry.source.label}
      </Link>
    );
  }
  if (entry.source) {
    return <p className="text-sm text-muted-foreground">Source: {entry.source.label}</p>;
  }
  if (entry.source_id == null || entry.source_id <= 0) return null;
  const id = String(entry.source_id);

  switch (entry.source_type) {
    case "invoice":
      return (
        <Link
          to="/app/$tenantSlug/accounting/sales/invoices/$id"
          params={{ tenantSlug, id }}
          className={linkClass}
        >
          <ExternalLink aria-hidden className="h-4 w-4" />
          View source invoice
        </Link>
      );
    case "bill":
      return (
        <Link
          to="/app/$tenantSlug/accounting/purchases/$id"
          params={{ tenantSlug, id }}
          className={linkClass}
        >
          <ExternalLink aria-hidden className="h-4 w-4" />
          View source bill
        </Link>
      );
    case "receipt":
      return (
        <Link
          to="/app/$tenantSlug/accounting/money/receipts/$id"
          params={{ tenantSlug, id }}
          className={linkClass}
        >
          <ExternalLink aria-hidden className="h-4 w-4" />
          View source receipt
        </Link>
      );
    case "payment":
      return (
        <Link
          to="/app/$tenantSlug/accounting/money/payments/$id"
          params={{ tenantSlug, id }}
          className={linkClass}
        >
          <ExternalLink aria-hidden className="h-4 w-4" />
          View source payment
        </Link>
      );
    default:
      return null;
  }
}

/** Trigger a Blob download (same pattern as the date-range Tally export). */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const TALLY_VOUCHER_HINT =
  "One TallyPrime voucher for this entry. Don't also import it through the date-range export or the payroll run's Tally JV — Tally would book it twice.";

/** "Tally voucher" — this one entry as a TallyPrime-importable voucher. */
function TallyVoucherButton({ entry }: Readonly<{ entry: JournalEntryDetail }>) {
  const [downloading, setDownloading] = useState(false);
  // The date-range export's "one ledger per customer and vendor" option.
  const [partyLedgers, setPartyLedgers] = useState(false);

  async function onDownload() {
    setDownloading(true);
    try {
      const blob = await accountingApi.downloadJournalTallyXML(entry.id, partyLedgers);
      downloadBlob(blob, `tally-voucher-${entry.entry_no}.xml`);
    } catch (err) {
      toast.error(`Could not download the Tally voucher: ${toApiError(err).message}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id="journal-tally-party"
          checked={partyLedgers}
          onCheckedChange={(v) => setPartyLedgers(v === true)}
          data-testid="journal-tally-party-ledgers"
        />
        <Label
          htmlFor="journal-tally-party"
          className="flex items-center text-sm font-normal [@media(pointer:coarse)]:min-h-11"
          title="Receivables and payables post to each party's own ledger (its contact name, under Sundry Debtors / Creditors in Tally) instead of Trade Receivables / Trade Payables."
        >
          One Ledger per Customer / Vendor
        </Label>
      </div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => void onDownload()}
              disabled={downloading}
              data-testid="journal-tally-voucher"
            >
              {downloading ? (
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              ) : (
                <Download aria-hidden className="h-4 w-4" />
              )}
              Tally Voucher
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{TALLY_VOUCHER_HINT}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function JournalDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useJournalEntry(numId, validId);
  const entry = q.data;
  const isSmPlus = useSmPlus();

  return (
    <AccountingModuleShell
      title={entry ? `Journal #${entry.entry_no}` : "Journal"}
      description={entry?.narration}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/accounting/journals"
              params={{ tenantSlug }}
            >
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back to Journals
            </Link>
          </Button>
          {entry && <TallyVoucherButton entry={entry} />}
        </div>
      }
    >
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid journal id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid journal entry id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this journal entry</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/accounting/journals"
              params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to Journals
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && entry && (
        <div className="space-y-5">
          {/* Header */}
          <Card>
            <CardContent className="space-y-4 py-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <HeaderField label="Entry #" value={String(entry.entry_no)} />
                <HeaderField label="Date" value={fmtDate(entry.date)} />
                <div className="space-y-0.5">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Source
                  </p>
                  <SourceBadge source={entry.source_type} />
                </div>
                <HeaderField
                  label="Posted"
                  value={entry.posted_at ? fmtDateTime(entry.posted_at) : "—"}
                />
              </div>
              <div className="space-y-0.5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Narration
                </p>
                <p className="text-sm text-gray-900">
                  {entry.narration || "—"}
                </p>
              </div>
              {(entry.source || (entry.source_id != null && entry.source_id > 0)) && (
                <SourceDocumentLink entry={entry} tenantSlug={tenantSlug} />
              )}
            </CardContent>
          </Card>

          {/* Lines — balanced Dr/Cr table. Phones: one row per line —
              account and the Dr / Cr amount on top, contact and
              description underneath; totals as a summary row. */}
          {!isSmPlus && (
          <ul className="divide-y rounded-md border" data-testid="journal-lines-cards">
            {entry.lines.map((line) => (
              <li key={line.id} className="space-y-1 px-3 py-2.5 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 break-words font-medium text-gray-900">
                    {line.account_code
                      ? `${line.account_code} — ${line.account_name ?? ""}`.trim()
                      : (line.account_name ?? "—")}
                  </span>
                  <span className="shrink-0 whitespace-nowrap tabular-nums">
                    {line.debit_minor > 0 ? (
                      <>
                        {formatPaise(line.debit_minor)}{" "}
                        <span className="text-xs text-muted-foreground">Dr</span>
                      </>
                    ) : line.credit_minor > 0 ? (
                      <>
                        {formatPaise(line.credit_minor)}{" "}
                        <span className="text-xs text-muted-foreground">Cr</span>
                      </>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>
                {(line.contact_name || line.description) && (
                  <p className="break-words text-xs text-muted-foreground">
                    {[line.contact_name, line.description].filter(Boolean).join(" · ")}
                  </p>
                )}
              </li>
            ))}
            <li className="grid grid-cols-2 gap-3 bg-muted/40 px-3 py-2.5 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Total Debits</p>
                <p className="font-semibold tabular-nums">{formatPaise(entry.total_debit)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total Credits</p>
                <p className="font-semibold tabular-nums">{formatPaise(entry.total_credit)}</p>
              </div>
            </li>
          </ul>
          )}
          {isSmPlus && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entry.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <div className="space-y-0.5">
                        <p className="font-medium text-gray-900">
                          {line.account_code
                            ? `${line.account_code} — ${line.account_name ?? ""}`.trim()
                            : (line.account_name ?? "—")}
                        </p>
                        {line.contact_name && (
                          <p className="text-xs text-muted-foreground">
                            {line.contact_name}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {line.description || "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {line.debit_minor > 0
                        ? formatPaise(line.debit_minor)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {line.credit_minor > 0
                        ? formatPaise(line.credit_minor)
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2} className="font-medium">
                    Totals
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {formatPaise(entry.total_debit)}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {formatPaise(entry.total_credit)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
          )}
        </div>
      )}
    </AccountingModuleShell>
  );
}
